import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react'
import { useParams } from 'react-router-dom'
import { CompanySurveyService } from '@common/services/company_survey_service'
import type { CompanySurveyForm } from '@common/models/project/company_survey_form_model'
import { useTownContext } from '@/context/TownContext'

export type ProjectFormState = {
  /** 現在の schoolId（URL から） */
  schoolId: string | undefined
  /** 現在の projectId（URL から） */
  projectId: string | undefined
  /** プロジェクトフォーム（getForm の結果）。未取得・取得失敗時は null */
  form: CompanySurveyForm | null
  /** 取得中かどうか */
  loading: boolean
  /** 取得エラーメッセージ。なしなら null */
  error: string | null
}

type ProjectFormContextValue = ProjectFormState

const ProjectFormContext = createContext<ProjectFormContextValue | undefined>(undefined)

/**
 * 同一プロジェクト（schoolId + projectId）のフォーム情報を1回だけ取得し、
 * 子コンポーネントで共有するためのプロバイダ。
 * ルートの :schoolId/:projectId の直下で使用すること。
 */
export function ProjectFormProvider({ children }: { children: ReactNode }) {
  const { schoolId, projectId } = useParams<{ schoolId: string; projectId: string }>()
  const { setDisplayInfoFromForm } = useTownContext()

  const [form, setForm] = useState<CompanySurveyForm | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!schoolId || !projectId) {
      setForm(null)
      setLoading(false)
      setError(null)
      return
    }

    let cancelled = false
    setLoading(true)
    setError(null)

    const load = async () => {
      try {
        const surveyService = new CompanySurveyService()
        const formRes = await surveyService.getForm(schoolId, projectId)
        if (cancelled) return
        if (formRes) {
          setForm(formRes)
          setDisplayInfoFromForm(formRes)
        } else {
          setForm(null)
          setError('フォームが見つかりません。')
        }
      } catch (err) {
        if (!cancelled) {
          console.error('プロジェクトフォーム取得エラー:', err)
          setError('データの取得に失敗しました。しばらく経ってからお試しください。')
          setForm(null)
        }
      } finally {
        if (!cancelled) setLoading(false)
      }
    }

    load()
    return () => {
      cancelled = true
    }
  }, [schoolId, projectId, setDisplayInfoFromForm])

  const value = useMemo<ProjectFormContextValue>(
    () => ({
      schoolId,
      projectId,
      form,
      loading,
      error,
    }),
    [schoolId, projectId, form, loading, error]
  )

  return (
    <ProjectFormContext.Provider value={value}>
      {children}
    </ProjectFormContext.Provider>
  )
}

export function useProjectFormContext(): ProjectFormContextValue {
  const context = useContext(ProjectFormContext)
  if (context === undefined) {
    throw new Error('useProjectFormContext must be used within ProjectFormProvider')
  }
  return context
}
